// ─────────────────────────────────────────────────────────────
// AuthorAvatar — circle with a contributor's initials.
// Used by CommunityTab (top-level Community lanes) and
// WelcomeTab (featured-scripts bento cell). Extracted so both
// surfaces stay in visual lockstep: same color hashing, same
// proportions, same dark/light tones.
//
// The background color is hashed deterministically from the
// name so the same person always gets the same avatar tone
// across the page — light mode pastel, dark mode muted.
// ─────────────────────────────────────────────────────────────

import { Box, useTheme } from "@mui/material";
import { extractFirstName, extractLastName } from "./AiPanel";

// Author initials for the avatar circle. Uses the comma-aware helpers
// shared with the Marie chat avatar and the app header so the corporate
// TF SSO "Lastname, Firstname [M]" format produces "GC" for
// "Clark, Greg J" (not "CJ"). "Firstname Lastname" and single-name
// inputs continue to work correctly.
export function authorInitials(name: string): string {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const first = extractFirstName(trimmed);
  const last = extractLastName(trimmed);
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first[0].toUpperCase();
  return "?";
}

// HSL hue derived from a simple string hash. Pinned saturation +
// lightness so all avatar tones feel like they belong together.
export function authorAvatarColor(name: string, isDark: boolean): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return isDark ? `hsl(${hue} 45% 38%)` : `hsl(${hue} 55% 78%)`;
}

export interface AuthorAvatarProps {
  name: string;
  size?: number;
}

export function AuthorAvatar({ name, size = 28 }: AuthorAvatarProps) {
  const theme = useTheme();
  const bg = authorAvatarColor(name, theme.palette.mode === "dark");
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: bg,
        color: theme.palette.mode === "dark" ? "#fff" : "#1e1e1e",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.42,
        fontWeight: 600,
        flexShrink: 0,
        userSelect: "none",
      }}
    >
      {authorInitials(name)}
    </Box>
  );
}
